import { useMemo, useState, type FormEvent } from 'react';
import { addDays, type HomeworkTarget, type LocalDate, type MessageKey } from '@hamro/shared';
import { useT, useLocale } from '../lib/i18n.js';
import { useSession } from '../lib/session.js';
import {
  useCreateHomework,
  useHomework,
  useHomeworkTargets,
  useSchoolContext,
} from '../lib/queries.js';
import { ApiRequestError } from '../lib/api.js';
import { PageHeader } from '../components/PageHeader.js';
import { Panel } from '../components/Panel.js';
import { EmptyState } from '../components/EmptyState.js';
import { QueryState } from '../components/QueryState.js';
import { StatusPill } from '../components/StatusPill.js';
import { Toast } from '../components/Toast.js';
import { date as formatDate } from '../lib/format.js';

/**
 * Homework, grouped by the day it is due.
 *
 * Due dates are calendar dates, so "Friday" means Friday at the school for
 * everyone reading — including a parent in another timezone, who would
 * otherwise be told their child's homework is due a day early.
 */
export default function HomeworkPage() {
  const t = useT();
  const locale = useLocale();
  const { can } = useSession();
  const { data: context } = useSchoolContext();
  const homework = useHomework();
  const [toast, setToast] = useState<{ kicker: string; detail: string; tone: 'ink' | 'stamp' } | null>(
    null,
  );

  const canPost = can('homework:write');

  const groups = groupByDueDate(homework.data ?? []);

  return (
    <>
      <PageHeader title={t('page.homework.title')} subtitle={t('page.homework.subtitle')} />

      {canPost && <Compose today={context?.today ?? null} onResult={setToast} />}

      <QueryState
        isLoading={homework.isLoading}
        error={homework.error}
        onRetry={() => void homework.refetch()}
      >
        {groups.length === 0 ? (
          <Panel>
            <EmptyState message={canPost ? 'page.homework.empty' : 'page.homework.empty_parent'} />
          </Panel>
        ) : (
          <div className="grid gap-4">
            {groups.map(([dueDate, posts]) => (
              <Panel
                key={dueDate}
                title={t('page.homework.due', { date: formatDate(dueDate, locale) })}
                meta={`${posts.length}`}
                action={
                  // Marigold means pending, here as everywhere: work still due.
                  context && dueDate >= context.today ? (
                    <StatusPill label={t('column.due')} tone="marigold" />
                  ) : undefined
                }
              >
                <ul>
                  {posts.map((post) => (
                    <li
                      key={post.id}
                      className="border-b border-rule-soft px-4 py-3 last:border-b-0 sm:px-5"
                    >
                      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                        {/* Subject and section are school-authored; shown verbatim. */}
                        <span className="text-[14.5px] font-medium">{post.subjectName}</span>
                        <span className="font-mono text-[10.5px] text-ink-45">
                          {post.sectionName}
                        </span>
                        <span className="ml-auto font-mono text-[10.5px] text-ink-45">
                          {post.postedByName}
                        </span>
                      </div>
                      {post.title && (
                        <div className="mt-0.5 text-[14px] text-ink-70">{post.title}</div>
                      )}
                      <p className="mt-0.5 text-[13.5px] whitespace-pre-line text-ink-70">
                        {post.body}
                      </p>
                    </li>
                  ))}
                </ul>
              </Panel>
            ))}
          </div>
        )}
      </QueryState>

      {toast && (
        <Toast
          kicker={toast.kicker}
          detail={toast.detail}
          tone={toast.tone}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}

type Notify = (toast: { kicker: string; detail: string; tone: 'ink' | 'stamp' }) => void;

/**
 * Setting homework.
 *
 * The class and subject pickers come from the server rather than from the
 * school's full timetable: a teacher's options are their teaching assignments,
 * and offering a class the save would refuse is a worse experience than not
 * offering it. Choosing a class narrows the subjects, because the pair is what
 * the permission is actually about.
 *
 * The form sits open above the list rather than behind a modal — a teacher
 * setting Friday's work for three classes does it three times in a row.
 */
function Compose({ today, onResult }: { today: string | null; onResult: Notify }) {
  const t = useT();
  const targets = useHomeworkTargets(true);
  const create = useCreateHomework();

  const [sectionId, setSectionId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notifyGuardians, setNotifyGuardians] = useState(true);

  const rows = useMemo<HomeworkTarget[]>(() => targets.data ?? [], [targets.data]);
  const selected = rows.find((row) => row.sectionId === sectionId) ?? rows[0];
  const subjects = selected?.subjects ?? [];

  // Tomorrow is what most homework is due, so it is the default rather than a
  // blank a teacher has to fill in every time.
  const defaultDue = today ? addDays(today as LocalDate, 1) : '';
  const effectiveDue = dueDate || defaultDue;
  const effectiveSection = selected?.sectionId ?? '';
  const effectiveSubject =
    subjects.find((subject) => subject.id === subjectId)?.id ?? subjects[0]?.id ?? '';

  if (targets.isLoading) return null;

  if (rows.length === 0) {
    return (
      <div className="mb-4">
        <Panel title={t('compose.homework.title')}>
          <EmptyState message="compose.homework.none" compact />
        </Panel>
      </div>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await create.mutateAsync({
        sectionId: effectiveSection,
        subjectId: effectiveSubject,
        title: title.trim() || undefined,
        body: body.trim(),
        dueDate: effectiveDue,
        notifyGuardians,
      });
      const section = selected ? `${selected.gradeLevelName} ${selected.sectionName}` : '';
      onResult({
        kicker: t('compose.homework.done'),
        detail: t('compose.homework.done_detail', { section }),
        tone: 'ink',
      });
      // The class and the due date survive, the work does not: the next post is
      // usually the same class, a different exercise.
      setTitle('');
      setBody('');
    } catch (error) {
      onResult({
        kicker: t('error.generic'),
        detail: error instanceof ApiRequestError ? t(error.key as MessageKey) : t('error.generic'),
        tone: 'stamp',
      });
    }
  }

  return (
    <div className="mb-4">
      <Panel title={t('compose.homework.title')}>
        <form className="grid gap-3 px-4 py-3.5 sm:px-5" onSubmit={submit}>
          <div className="flex flex-wrap gap-2">
            <label className="min-w-[150px] flex-1">
              <span className="field-label mb-1 block">{t('compose.homework.class')}</span>
              <select
                className="input"
                value={effectiveSection}
                onChange={(event) => {
                  setSectionId(event.target.value);
                  // The old subject may not be taught in the new class.
                  setSubjectId('');
                }}
              >
                {rows.map((row) => (
                  <option key={row.sectionId} value={row.sectionId}>
                    {row.gradeLevelName} {row.sectionName}
                  </option>
                ))}
              </select>
            </label>

            <label className="min-w-[150px] flex-1">
              <span className="field-label mb-1 block">{t('compose.homework.subject')}</span>
              <select
                className="input"
                value={effectiveSubject}
                onChange={(event) => setSubjectId(event.target.value)}
              >
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="w-[170px]">
              <span className="field-label mb-1 block">{t('compose.homework.due')}</span>
              <input
                className="input font-mono"
                type="date"
                value={effectiveDue}
                onChange={(event) => setDueDate(event.target.value)}
                required
              />
            </label>
          </div>

          <label>
            <span className="field-label mb-1 block">
              {t('compose.homework.heading')} · {t('compose.homework.heading_hint')}
            </span>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>

          <label>
            <span className="field-label mb-1 block">{t('compose.homework.body')}</span>
            <textarea
              className="input min-h-[76px] resize-y"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
            />
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-[13.5px] text-ink-70">
              <input
                type="checkbox"
                className="size-4 accent-ink"
                checked={notifyGuardians}
                onChange={(e) => setNotifyGuardians(e.target.checked)}
              />
              {t('compose.homework.notify')}
            </label>
            <button
              type="submit"
              className="btn-primary ml-auto"
              disabled={create.isPending || !body.trim() || !effectiveSubject}
            >
              {create.isPending ? t('action.setting_homework') : t('action.set_homework')}
            </button>
          </div>
        </form>
      </Panel>
    </div>
  );
}

function groupByDueDate<T extends { dueDate: string }>(posts: T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const post of posts) {
    const bucket = groups.get(post.dueDate) ?? [];
    bucket.push(post);
    groups.set(post.dueDate, bucket);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}
